import React, { useState } from "react";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  InputBase,
  Badge,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  Menu as MenuIcon,
  AccountCircle,
  CloudUpload,
  Home,
  Logout,
  Login,
  Close,
  Settings,
  Explore,
  DynamicFeed,
  Videocam as VideocamIcon,
  NotificationsOutlined,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { useNotifications } from "../../contexts/NotificationContext";
import { formatBadgeCount, getBadgeAriaLabel } from "../../utils/notificationUtils";
import InstallPrompt from "../PWA/InstallPrompt";
import ThemePicker from "./ThemePicker";
import HooliahLogo from "./HooliahLogo";
import { GLASS } from "../../theme";
import { useThemeStore } from "../../stores/themeStore";

/**
 * Nav link styling helper — returns sx props for a navigation link button
 * with theme-aware active/inactive colors and hover underline animation.
 */
const getNavLinkSx = (isActive: boolean, isDark: boolean) => ({
  position: "relative" as const,
  color: isActive
    ? "primary.main"
    : "text.primary",
  fontWeight: isActive && !isDark ? 700 : isActive ? 600 : 400,
  textDecoration: "none",
  mx: 0.5,
  pb: 0.5,
  "&::after": {
    content: '""',
    position: "absolute" as const,
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%) scaleX(0)",
    width: "80%",
    height: "2px",
    backgroundColor: "primary.main",
    transition: "transform 200ms ease",
    transformOrigin: "center",
  },
  "&:hover::after": {
    transform: "translateX(-50%) scaleX(1)",
  },
  "@media (prefers-reduced-motion: reduce)": {
    "&::after": {
      transition: "none",
    },
  },
});

/**
 * Header component with navigation, search, theme picker, and glassmorphism styling.
 * Adapts to the active theme (Cyber-Street dark or Modern-Minimalist light).
 */
const Header: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const hasBottomNav = useMediaQuery('(max-width:767px)');
  const { user, isAuthenticated, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const palette = useThemeStore((state) => state.palette);
  const isDarkMode = palette === "cyber-street" || palette === "dark-luxe" || palette === "editorial-neutral";
  const glass = GLASS[palette];

  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  // Sync search query with URL when on search page with ai=true
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlQuery = params.get("q") || "";
    const isAiSearch = params.get("ai") === "true";

    // Only update search bar if it's an AI search
    if (isAiSearch && urlQuery) {
      setSearchQuery(urlQuery);
    } else if (!location.pathname.startsWith("/search")) {
      // Clear search bar when not on search page
      setSearchQuery("");
    }
  }, [location.search, location.pathname]);

  // Mobile drawer state
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // User menu state
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const isUserMenuOpen = Boolean(userMenuAnchor);

  // Handle search submit - Top search bar uses AI search
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const query = searchQuery.trim();
      navigate(`/search?q=${encodeURIComponent(query)}&ai=true`);
    }
  };

  // Handle search input key down
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearchSubmit(e as any);
    }
  };

  // Handle user menu open
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  // Handle user menu close
  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  // Handle logout
  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    navigate("/");
  };

  // Handle mobile drawer toggle
  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  // Check if a route is active
  const isRouteActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  // Navigation links config
  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/search", label: "Browse" },
    { to: "/discover", label: "Community" },
    { to: "/feed", label: "Feed" },
    { to: "/blog", label: "Blog" },
    { to: "/live", label: "Live" },
    { to: "/notifications", label: "Notifications" },
  ];

  // Mobile drawer content
  const mobileDrawerContent = (
    <Box sx={{ width: 250, pt: 2, bgcolor: 'background.default', height: '100%' }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2 }}>
        <ThemePicker />
        <IconButton onClick={toggleMobileDrawer} sx={{ color: 'text.primary' }}>
          <Close />
        </IconButton>
      </Box>
      <List>
        <ListItem
          component={RouterLink}
          to="/"
          onClick={toggleMobileDrawer}
          sx={{ cursor: "pointer", color: "text.primary", textDecoration: "none" }}
        >
          <ListItemIcon sx={{ color: "text.secondary" }}>
            <Home />
          </ListItemIcon>
          <ListItemText primary="Home" />
        </ListItem>

        <ListItem
          component={RouterLink}
          to="/discover"
          onClick={toggleMobileDrawer}
          sx={{ cursor: "pointer", color: "text.primary", textDecoration: "none" }}
        >
          <ListItemIcon sx={{ color: "text.secondary" }}>
            <Explore />
          </ListItemIcon>
          <ListItemText primary="Community" />
        </ListItem>

        <ListItem
          component={RouterLink}
          to="/feed"
          onClick={toggleMobileDrawer}
          sx={{ cursor: "pointer", color: "text.primary", textDecoration: "none" }}
        >
          <ListItemIcon sx={{ color: "text.secondary" }}>
            <DynamicFeed />
          </ListItemIcon>
          <ListItemText primary="Feed" />
        </ListItem>

        <ListItem
          component={RouterLink}
          to="/live"
          onClick={toggleMobileDrawer}
          sx={{ cursor: "pointer", color: "text.primary", textDecoration: "none" }}
        >
          <ListItemIcon sx={{ color: "text.secondary" }}>
            <VideocamIcon />
          </ListItemIcon>
          <ListItemText primary="Live Streams" />
        </ListItem>

        <ListItem
          component={RouterLink}
          to="/blog"
          onClick={toggleMobileDrawer}
          sx={{ cursor: "pointer", color: "text.primary", textDecoration: "none" }}
        >
          <ListItemIcon sx={{ color: "text.secondary" }}>
            <DynamicFeed />
          </ListItemIcon>
          <ListItemText primary="Blog" />
        </ListItem>

        <ListItem
          component={RouterLink}
          to="/about"
          onClick={toggleMobileDrawer}
          sx={{ cursor: "pointer", color: "text.primary", textDecoration: "none" }}
        >
          <ListItemIcon sx={{ color: "text.secondary" }}>
            <Explore />
          </ListItemIcon>
          <ListItemText primary="About" />
        </ListItem>

        <ListItem
          component={RouterLink}
          to="/faq"
          onClick={toggleMobileDrawer}
          sx={{ cursor: "pointer", color: "text.primary", textDecoration: "none" }}
        >
          <ListItemIcon sx={{ color: "text.secondary" }}>
            <Explore />
          </ListItemIcon>
          <ListItemText primary="FAQ" />
        </ListItem>

        <ListItem
          component={RouterLink}
          to="/notifications"
          onClick={toggleMobileDrawer}
          sx={{ cursor: "pointer", color: "text.primary", textDecoration: "none" }}
        >
          <ListItemIcon sx={{ color: "text.secondary" }}>
            <Badge
              badgeContent={formatBadgeCount(unreadCount)}
              color="error"
              invisible={!unreadCount || unreadCount === 0}
              max={99}
              aria-label={getBadgeAriaLabel(unreadCount)}
            >
              <NotificationsOutlined />
            </Badge>
          </ListItemIcon>
          <ListItemText primary="Notifications" />
        </ListItem>

        {isAuthenticated ? (
          <>
            <ListItem
              component={RouterLink}
              to={`/profile/${user?.id || "me"}`}
              onClick={toggleMobileDrawer}
              sx={{ cursor: "pointer", color: "text.primary", textDecoration: "none" }}
            >
              <ListItemIcon sx={{ color: "text.secondary" }}>
                <AccountCircle />
              </ListItemIcon>
              <ListItemText primary="My Profile" />
            </ListItem>

            <ListItem
              component={RouterLink}
              to="/upload"
              onClick={toggleMobileDrawer}
              sx={{ cursor: "pointer", color: "text.primary", textDecoration: "none" }}
            >
              <ListItemIcon sx={{ color: "text.secondary" }}>
                <CloudUpload />
              </ListItemIcon>
              <ListItemText primary="Upload" />
            </ListItem>

            <ListItem
              component={RouterLink}
              to="/settings"
              onClick={toggleMobileDrawer}
              sx={{ cursor: "pointer", color: "text.primary", textDecoration: "none" }}
            >
              <ListItemIcon sx={{ color: "text.secondary" }}>
                <Settings />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItem>

            <Divider />

            <ListItem
              onClick={() => {
                toggleMobileDrawer();
                logout();
              }}
              sx={{ cursor: "pointer", color: "text.primary" }}
            >
              <ListItemIcon sx={{ color: "text.secondary" }}>
                <Logout />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </>
        ) : (
          <ListItem
            component={RouterLink}
            to="/login"
            onClick={toggleMobileDrawer}
            sx={{ cursor: "pointer", color: "text.primary", textDecoration: "none" }}
          >
            <ListItemIcon sx={{ color: "text.secondary" }}>
              <Login />
            </ListItemIcon>
            <ListItemText primary="Login" />
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: glass.navBar.background,
        backdropFilter: glass.navBar.blur,
        WebkitBackdropFilter: glass.navBar.blur,
        borderBottom: glass.navBar.border,
        boxShadow: "none",
        "@supports not (backdrop-filter: blur(1px))": {
          backgroundColor: isDarkMode
            ? "rgba(18, 18, 18, 0.95)"
            : "rgba(255, 255, 255, 0.95)",
        },
      }}
    >
      <Toolbar>
        {isMobile && !hasBottomNav && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleMobileDrawer}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* App logo — theme-aware SVG, fixed width on mobile */}
        <Box sx={{ flexGrow: 0, flexShrink: 0, mr: { xs: 0.5, md: 4 }, display: 'flex', alignItems: 'center' }}>
          <HooliahLogo height={96} />
        </Box>

        {/* Desktop nav links with theme-aware colors and hover underline */}
        {!isMobile &&
          navLinks.map((link) => {
            if (link.label === "Notifications") {
              const badgeAriaLabel = getBadgeAriaLabel(unreadCount);
              return (
                <Button
                  key={link.to}
                  component={RouterLink}
                  to={link.to}
                  disableRipple
                  sx={getNavLinkSx(isRouteActive(link.to), isDarkMode)}
                  startIcon={
                    <Badge
                      badgeContent={formatBadgeCount(unreadCount)}
                      color="error"
                      invisible={!unreadCount || unreadCount === 0}
                      max={99}
                      aria-label={badgeAriaLabel}
                    >
                      <NotificationsOutlined fontSize="small" />
                    </Badge>
                  }
                >
                  {link.label}
                </Button>
              );
            }
            return (
              <Button
                key={link.to}
                component={RouterLink}
                to={link.to}
                disableRipple
                sx={getNavLinkSx(isRouteActive(link.to), isDarkMode)}
              >
                {link.label}
              </Button>
            );
          })}

        {/* Search input — transparent bg, 12% border, primary color on focus */}
        <Box
          component="form"
          onSubmit={handleSearchSubmit}
          sx={{
            position: "relative",
            borderRadius: theme.shape.borderRadius,
            backgroundColor: "transparent",
            border: `1px solid ${
              isDarkMode
                ? "rgba(245, 245, 247, 0.12)"
                : "rgba(28, 28, 28, 0.12)"
            }`,
            "&:focus-within": {
              borderColor: "primary.main",
            },
            transition: "border-color 200ms ease",
            ml: { xs: 0, md: 2 },
            flexGrow: 1,
            maxWidth: { xs: "100%", md: 400 },
            mx: { xs: 1, md: "auto" },
            "@media (prefers-reduced-motion: reduce)": {
              transition: "none",
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              height: "100%",
              display: "flex",
              alignItems: "center",
              pl: 2,
            }}
          >
            <SearchIcon sx={{ color: "text.secondary" }} />
          </Box>
          <InputBase
            placeholder="AI Search outfits..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            sx={{
              color: "text.primary",
              width: "100%",
              "& .MuiInputBase-input": {
                padding: theme.spacing(1, 1, 1, 0),
                paddingLeft: `calc(1em + ${theme.spacing(4)})`,
                width: "100%",
              },
            }}
          />
        </Box>

        {/* User avatar / login button on mobile — right side after search */}
        {isMobile && hasBottomNav && (
          isAuthenticated ? (
            <IconButton
              onClick={() => navigate(`/profile/${user?.id || "me"}`)}
              size="small"
              aria-label="account"
              sx={{ ml: 0.5 }}
            >
              {user?.profilePicture ? (
                <Avatar
                  src={user.profilePicture}
                  alt={user.displayName}
                  sx={{ width: 28, height: 28 }}
                />
              ) : (
                <AccountCircle />
              )}
            </IconButton>
          ) : (
            <IconButton
              component={RouterLink}
              to="/login"
              size="small"
              aria-label="login"
              sx={{ ml: 0.5, color: 'primary.main' }}
            >
              <AccountCircle />
            </IconButton>
          )
        )}

        {/* Desktop action area: InstallPrompt, ThemePicker, Upload CTA, User avatar */}
        {!isMobile && (
          <Box sx={{ display: "flex", ml: 2, alignItems: "center", gap: 1 }}>
            <InstallPrompt showInHeader />

            {/* ThemePicker — between search and user avatar */}
            <ThemePicker />

            {isAuthenticated ? (
              <>
                {/* Upload CTA — primary action color bg, background color text */}
                <Button
                  variant="contained"
                  color="primary"
                  component={RouterLink}
                  to="/upload"
                  startIcon={<CloudUpload />}
                  sx={{
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": {
                      backgroundColor: "primary.main",
                    },
                  }}
                >
                  Upload
                </Button>

                <IconButton
                  onClick={handleUserMenuOpen}
                  size="small"
                  edge="end"
                  aria-label="account"
                  aria-controls="user-menu"
                  aria-haspopup="true"
                >
                  {user?.profilePicture ? (
                    <Avatar
                      src={user.profilePicture}
                      alt={user.displayName}
                      sx={{ width: 32, height: 32 }}
                    />
                  ) : (
                    <AccountCircle />
                  )}
                </IconButton>

                <Menu
                  id="user-menu"
                  anchorEl={userMenuAnchor}
                  open={isUserMenuOpen}
                  onClose={handleUserMenuClose}
                  transformOrigin={{ horizontal: "right", vertical: "top" }}
                  anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                >
                  <MenuItem
                    component={RouterLink}
                    to={`/profile/${user?.id || "me"}`}
                    onClick={handleUserMenuClose}
                  >
                    My Profile
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to={`/profile/${user?.id || "me"}?tab=0`}
                    onClick={handleUserMenuClose}
                  >
                    My Outfits
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/settings"
                    onClick={handleUserMenuClose}
                  >
                    Settings
                  </MenuItem>

                  <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                color="primary"
                variant="contained"
                component={RouterLink}
                to="/login"
              >
                Login
              </Button>
            )}
          </Box>
        )}
      </Toolbar>

      {/* Mobile drawer — preserved for < 960px */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={toggleMobileDrawer}
      >
        {mobileDrawerContent}
      </Drawer>
    </AppBar>
  );
};

export default Header;
